import { useState } from 'react';
import { bt, btp } from '../../i18n';
import type { ComponentType, SVGProps } from 'react';
import type { IconField } from '@aquilixshop/ui-react';
import {
  CashDollarIcon,
  ChatIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  DeliveryIcon,
  DiscountIcon,
  GiftCardIcon,
  GlobeIcon,
  HeartIcon,
  PackageIcon,
  ReturnIcon,
  ShieldCheckMarkIcon,
  StarFilledIcon,
} from '@shopify/polaris-icons';
import { FieldRow } from '../FieldRow';

/**
 * Icon picker (deep-config infra). The merchant picks from a small CURATED set
 * of commerce-relevant glyphs (shipping / guarantee / returns / value-prop
 * badges); the stored value is the icon's stable `id` STRING — a plain
 * SettingValue, so nothing in the persistence/value layer changes.
 *
 * The set is owned HERE (the builder control), not the section schema, so
 * adding a glyph never re-versions a section. Each entry pins:
 *   - `id`    — persisted, forever-stable (renaming = a new id)
 *   - `label` — the merchant-facing name (accessible name of the swatch)
 *   - `Glyph` — the Polaris icon component used for the preview
 *
 * No native popup — the grid renders inline as a `radiogroup` (project rule).
 * Commits immediately (discrete control).
 */
type PolarisIcon = ComponentType<SVGProps<SVGSVGElement>>;

interface CuratedIcon {
  id: string;
  label: string;
  Glyph: PolarisIcon;
}

/** The curated set. Stable `id`s are the persisted contract — append, never rename. */
export const ICON_SET: readonly CuratedIcon[] = [
  { id: 'delivery', label: 'Delivery', Glyph: DeliveryIcon },
  { id: 'package', label: 'Package', Glyph: PackageIcon },
  { id: 'return', label: 'Returns', Glyph: ReturnIcon },
  { id: 'shield-check', label: 'Guarantee', Glyph: ShieldCheckMarkIcon },
  { id: 'check-circle', label: 'Verified', Glyph: CheckCircleIcon },
  { id: 'clock', label: 'Fast', Glyph: ClockIcon },
  { id: 'star', label: 'Quality', Glyph: StarFilledIcon },
  { id: 'heart', label: 'Loved', Glyph: HeartIcon },
  { id: 'gift', label: 'Gift', Glyph: GiftCardIcon },
  { id: 'discount', label: 'Discount', Glyph: DiscountIcon },
  { id: 'cash', label: 'Cash', Glyph: CashDollarIcon },
  { id: 'credit-card', label: 'Card', Glyph: CreditCardIcon },
  { id: 'chat', label: 'Support', Glyph: ChatIcon },
  { id: 'globe', label: 'Worldwide', Glyph: GlobeIcon },
] as const;

export function IconControl({
  field,
  value,
  onChange,
}: {
  field: IconField;
  value: string | undefined;
  onChange: (next: string) => void;
}) {
  const stored = value ?? field.default;
  // A stale stored id (no longer in the set) shows as "no selection" rather than crashing.
  const known = ICON_SET.some((i) => i.id === stored);
  const current = known ? stored : undefined;
  const [hovered, setHovered] = useState<string | null>(null);
  const previewIcon = ICON_SET.find((i) => i.id === (hovered ?? current));
  const previewLabel = previewIcon
    ? bt(`chrome.iconset.${previewIcon.id}`, previewIcon.label)
    : bt('chrome.common.none', 'None');

  return (
    <FieldRow id={field.id} label={field.label} info={field.info} labelAs="span">
      <div className="bld-iconpicker">
        <div
          className="bld-iconpicker__grid"
          role="radiogroup"
          aria-label={btp('chrome.icon.aria', '{name} icon', { name: field.label })}
          onMouseLeave={() => setHovered(null)}
        >
          {ICON_SET.map(({ id, label: floor, Glyph }) => {
            const label = bt(`chrome.iconset.${id}`, floor);
            const selected = id === current;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={label}
                title={label}
                className="bld-iconpicker__opt"
                onMouseEnter={() => setHovered(id)}
                onFocus={() => setHovered(id)}
                onClick={() => onChange(id)}
              >
                <Glyph width={20} height={20} aria-hidden="true" focusable={false} />
              </button>
            );
          })}
        </div>
        <p className="bld-iconpicker__name">{previewLabel}</p>
      </div>
    </FieldRow>
  );
}
